import { HEADER } from '.';
import { fixedEncodeURIComponent, type DocumentParameters, type FetchClient } from './http';
import type { BaseMetadataDocument, UploadPart } from './types/common';
import type { Content } from './types/contents';
import type { EOCRResultFormat } from './types/documents';

export const retrieveContentSize = ({
  client,
  workspaceId,
  documentId,
  versionId = 'last',
  mimeType,
}: { client: FetchClient } & DocumentParameters & { mimeType: string; versionId?: string }): Promise<number> => {
  return client.head(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/do-view`,
    mimeType
  );
};

export const downloadContent = ({
  client,
  workspaceId,
  documentId,
  versionId = 'last',
  mimeType,
  range,
}: { client: FetchClient } & DocumentParameters & {
    mimeType: string;
    versionId?: string;
    range?: string;
  }): Promise<Response> => {
  return client.download(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/do-view`,
    mimeType,
    range
  );
};

export const uploadContent = ({
  client,
  workspaceId,
  documentId,
  file,
  fileName,
  contentLength,
  contentRange,
  contentType,
  rightId,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  file?: File | BlobPart;
  contentLength?: number;
  contentType?: string;
  fileName?: string;
  contentRange?: string;
  rightId?: string;
}): Promise<Content | { uploadedParts: UploadPart[] }> => {
  let uploadUrl = `${client.baseUrl}/upload/workspaces/${workspaceId}/documents/${documentId}/contents`;

  if (rightId) {
    uploadUrl += `?rightId=${rightId}`;
  }

  contentType = contentType || (file instanceof File ? file.type : undefined);
  fileName = fileName || (file instanceof File ? file.name : undefined);
  contentLength = contentLength || (file instanceof File ? file.size : undefined);

  if (!contentType) {
    throw new Error(`The content type is mandatory to provide when uploading a content.`);
  }

  if (!fileName && contentRange !== 'reset') {
    throw new Error(`The file name is mandatory to provide when uploading a content.`);
  }

  if (!contentLength && contentRange !== 'reset') {
    throw new Error(`The content length is mandatory to provide when uploading a content.`);
  }

  const extraHeaders: Record<string, string> = {
    'Content-Type': contentType,
    'Content-Length': `${contentLength}`,
    accept: HEADER.APPLICATION_JSON,
  };

  if (fileName) {
    extraHeaders['Content-Disposition'] = `binary;filename*=UTF-8''${fixedEncodeURIComponent(fileName)}`;
  }

  if (contentRange) {
    extraHeaders['Content-Range'] = contentRange;
  }

  return client.upload(uploadUrl, file as BodyInit, extraHeaders);
};

export const deleteContent = ({
  client,
  workspaceId,
  documentId,
  mimeType,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  mimeType: string;
}): Promise<BaseMetadataDocument> => {
  return client.deleteJson(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/contents?mimeType=${mimeType}`);
};

export const getDocumentContentOCR = ({
  client,
  workspaceId,
  documentId,
  versionId,
  mimeType,
  resultFormat,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  versionId: string;
  mimeType: string;
  resultFormat: EOCRResultFormat;
}): Promise<string> => {
  return client.getText(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/ocr/results?mimeType=${mimeType}`,
    resultFormat
  );
};

export const doContentOCR = ({
  client,
  workspaceId,
  documentId,
  versionId,
  contentType,
  abortSignal,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  versionId: string;
  contentType: string;
  abortSignal: AbortSignal;
}): Promise<void> => {
  return client.postVoid(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/do-ocr`,
    { contentType },
    abortSignal
  );
};

type DoAnalyzeParams = {
  workspaceId: string;
  documentId: string;
  versionId: string;
  contentType: string;
};

export const doAnalyzeContent = ({
  client,
  workspaceId,
  documentId,
  versionId,
  contentType,
}: DoAnalyzeParams & { client: FetchClient }): Promise<Record<string, any>> => {
  return client.post(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/do-analyse`, {
    contentType,
  });
};

export const deleteContentOCR = ({
  client,
  workspaceId,
  documentId,
  versionId,
  mimeType,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  versionId: string;
  mimeType: string;
}): Promise<boolean> => {
  return client.del(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents/ocr/results?mimeType=${mimeType}`
  );
};
